import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  requestNotificationPermission,
  getFCMToken,
  onMessageListener,
  isNotificationSupported,
  getNotificationPermission
} from '../services/pushNotificationService';

/**
 * Custom hook for managing push notifications
 * @returns {Object} Notification state and functions
 */
const usePushNotifications = () => {
  const [isSupported, setIsSupported] = useState(false);
  const [permission, setPermission] = useState('default');
  const [token, setToken] = useState(null);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [error, setError] = useState(null);
  const [latestMessage, setLatestMessage] = useState(null);

  // Check if notifications are supported
  useEffect(() => {
    setIsSupported(isNotificationSupported());
    setPermission(getNotificationPermission());
  }, []);

  /**
   * Subscribe to push notifications
   */
  const subscribe = useCallback(async () => {
    try {
      setError(null);

      // Check if supported
      if (!isSupported) {
        setError('Push notifications are not supported in this browser');
        return false;
      }

      // Request permission
      const permissionGranted = await requestNotificationPermission();
      setPermission(getNotificationPermission());

      if (!permissionGranted) {
        setError('Notification permission denied');
        return false;
      }

      // Get FCM token
      const fcmToken = await getFCMToken();
      
      if (!fcmToken) {
        setError('Failed to get notification token');
        return false;
      }

      setToken(fcmToken);

      // Send token to backend
      const accessToken = localStorage.getItem('access_token');
      if (accessToken) {
        await axios.post(
          '/api/notifications/subscribe',
          { fcm_token: fcmToken },
          { headers: { Authorization: `Bearer ${accessToken}` } }
        );
      }

      setIsSubscribed(true);
      console.log('Successfully subscribed to push notifications');
      return true;
    } catch (err) {
      console.error('Error subscribing to notifications:', err);
      setError(err.message || 'Failed to subscribe to notifications');
      return false;
    }
  }, [isSupported]);

  /**
   * Unsubscribe from push notifications
   */
  const unsubscribe = useCallback(async () => {
    try {
      setError(null);

      const accessToken = localStorage.getItem('access_token');
      if (accessToken && token) {
        await axios.post(
          '/api/notifications/unsubscribe',
          { fcm_token: token },
          { headers: { Authorization: `Bearer ${accessToken}` } }
        );
      }

      setIsSubscribed(false);
      setToken(null);
      console.log('Successfully unsubscribed from push notifications');
      return true;
    } catch (err) {
      console.error('Error unsubscribing from notifications:', err);
      setError(err.message || 'Failed to unsubscribe from notifications');
      return false;
    }
  }, [token]);

  /**
   * Listen for incoming messages
   */
  useEffect(() => {
    if (!isSupported || !isSubscribed) return;

    const unsubscribeFromMessages = onMessageListener((payload) => {
      console.log('Received foreground message:', payload);
      setLatestMessage(payload);
    });

    return () => {
      if (unsubscribeFromMessages) {
        unsubscribeFromMessages();
      }
    };
  }, [isSupported, isSubscribed]);

  /**
   * Check subscription status on mount
   */
  useEffect(() => {
    const checkSubscription = async () => {
      try {
        const accessToken = localStorage.getItem('access_token');
        if (accessToken && permission === 'granted') {
          const response = await axios.get('/api/notifications/subscription-status', {
            headers: { Authorization: `Bearer ${accessToken}` }
          });
          
          if (response.data.is_subscribed) {
            setIsSubscribed(true);
            setToken(response.data.fcm_token);
          }
        }
      } catch (err) {
        console.error('Error checking subscription status:', err);
      }
    };

    if (isSupported && permission === 'granted') {
      checkSubscription();
    }
  }, [isSupported, permission]);

  return {
    isSupported,
    permission,
    token,
    isSubscribed,
    error,
    latestMessage,
    subscribe,
    unsubscribe
  };
};

export default usePushNotifications;
